import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
        // Informational — neutral accent for transient/backend states (DSR F4).
        // Mirrors the destructive pattern: semantic color for text+icon on a card
        // background with a tinted border. --color-info tokens so light+dark both
        // render correctly with zero app overrides.
        info: "border-info/40 bg-card text-info [&>svg]:text-info *:data-[slot=alert-description]:text-info/90",
        // Warning — "slow down / busy" states (DSR F4). --color-warning tokens.
        warning:
          "border-warning/40 bg-card text-warning [&>svg]:text-warning *:data-[slot=alert-description]:text-warning/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Interrupting `role="alert"` message for something that just went wrong or needs attention now (`default` | `destructive`). For a persistent in-flow status, prerequisite, or hint that should not interrupt, prefer `Notice`: when in doubt, take `Notice`.
 */
function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
